import JSZip from "jszip";
import simplify from "simplify-js";

// Lê um arquivo KML/KMZ e devolve apenas os polígonos contidos nele.
// KML é XML puro (DOMParser); KMZ é um ZIP contendo um .kml (JSZip).
// A ordem das coordenadas no KML é lng,lat[,alt] — invertemos para {lat, long}.

const ZIP_MAGIC = [0x50, 0x4b, 0x03, 0x04]; // "PK\x03\x04"

// Tolerância base do Douglas-Peucker em graus decimais (~30 m).
export const SIMPLIFY_TOLERANCE = 0.0003;

export const PRECISION_LEVELS = [
  { id: 1, label: "Baixa", tolerance: 0.003, meters: 300 },
  { id: 2, label: "Média", tolerance: 0.001, meters: 100 },
  { id: 3, label: "Alta", tolerance: 0.0003, meters: 30 },
  { id: 4, label: "Original", tolerance: 0, meters: 0 },
];

export const DEFAULT_PRECISION = 3;

export function getPrecisionLevel(id) {
  return (
    PRECISION_LEVELS.find((l) => l.id === id) ||
    PRECISION_LEVELS.find((l) => l.id === DEFAULT_PRECISION)
  );
}

// Escala a tolerância pelo tamanho do arquivo: arquivos maiores têm mais pontos
// e precisam de simplificação mais agressiva para manter performance.
export function toleranceForFile(file) {
  const kb = (file?.size || 0) / 1024;
  if (kb <= 10000) return 0.0003; // ~30 m
  return 0.001; // ~100 m
}

export function toleranceFloorForFile(file) {
  const kb = (file?.size || 0) / 1024;
  if (kb <= 2000) return 0; // qualquer precisão é segura
  if (kb <= 10000) return 0.0003; // ~30 m
  return 0.001; // ~100 m
}

export function resolvePrecision(file, levelId) {
  const level = getPrecisionLevel(levelId);
  const floor = toleranceFloorForFile(file);
  const tolerance = Math.max(level.tolerance, floor);
  const clamped = tolerance > level.tolerance;

  const applied = clamped
    ? PRECISION_LEVELS.find((l) => l.tolerance === tolerance) || level
    : level;
  return { tolerance, level, applied, clamped };
}

// Paleta usada ao importar vários polígonos: cada área recebe uma cor diferente,
export const IMPORT_POLYGON_COLORS = [
  "#E53935", // vermelho
  "#1E88E5", // azul
  "#43A047", // verde
  "#FB8C00", // laranja
  "#8E24AA", // roxo
  "#00ACC1", // ciano
  "#FDD835", // amarelo
  "#c16e53", // marrom
  "#EC407A", // rosa
  "#3949AB", // índigo
];

// Reduz a quantidade de vértices com Douglas-Peucker (simplify-js usa {x, y}).
// Nunca devolve menos de 3 pontos — senão deixaria de ser um polígono válido.
export function simplifyPoints(points, tolerance = SIMPLIFY_TOLERANCE) {
  if (!tolerance) return points;
  if (!Array.isArray(points) || points.length <= 3) return points;
  const xy = points.map((p) => ({ x: p.lat, y: p.long }));
  const out = simplify(xy, tolerance, true).map((p) => ({
    lat: p.x,
    long: p.y,
  }));
  return out.length >= 3 ? out : points;
}

function readAsText(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => resolve(e.target.result);
    reader.onerror = (e) => reject(e);
    reader.readAsText(file);
  });
}

function readAsArrayBuffer(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => resolve(e.target.result);
    reader.onerror = (e) => reject(e);
    reader.readAsArrayBuffer(file);
  });
}

async function isKmz(file, buffer) {
  if (file?.name?.toLowerCase().endsWith(".kmz")) return true;
  if (file?.name?.toLowerCase().endsWith(".kml")) return false;
  // Sem extensão confiável: checa o magic number do ZIP.
  const head = new Uint8Array(buffer.slice(0, 4));
  return ZIP_MAGIC.every((b, i) => head[i] === b);
}

async function extractKmlText(file) {
  const buffer = await readAsArrayBuffer(file);
  if (await isKmz(file, buffer)) {
    const zip = await JSZip.loadAsync(buffer);
    const kmlEntry = Object.keys(zip.files).find((name) =>
      name.toLowerCase().endsWith(".kml"),
    );
    if (!kmlEntry) {
      throw new Error("O arquivo KMZ não contém nenhum arquivo .kml.");
    }
    return zip.files[kmlEntry].async("string");
  }
  // KML: relê como texto (o buffer já garante que o arquivo é legível).
  return readAsText(file);
}

// "lng,lat,alt lng,lat,alt ..." -> [{ lat, long }]
function parseCoordinates(text) {
  return (text || "")
    .trim()
    .split(/\s+/)
    .map((tuple) => {
      const parts = tuple.split(",");
      const long = Number(parts[0]);
      const lat = Number(parts[1]);
      return { lat, long };
    })
    .filter((p) => Number.isFinite(p.lat) && Number.isFinite(p.long));
}

// O anel do KML repete o primeiro vértice no final para fechar; remove a duplicata.
function dropClosingVertex(points) {
  if (points.length < 2) return points;
  const first = points[0];
  const last = points[points.length - 1];
  if (first.lat === last.lat && first.long === last.long) {
    return points.slice(0, -1);
  }
  return points;
}

function firstChildText(parent, tagName) {
  const node = parent.getElementsByTagName(tagName)[0];
  return node ? node.textContent.trim() : "";
}

/**
 * @param {File} file arquivo .kml ou .kmz
 * @param {{ tolerance?: number }} [options]
 * @returns {Promise<Array<{ name: string, points: Array<{lat:number, long:number}>, rawCount: number }>>}
 */
export async function parseKmlFile(file, { tolerance: override } = {}) {
  const text = await extractKmlText(file);
  const doc = new DOMParser().parseFromString(text, "application/xml");

  if (doc.getElementsByTagName("parsererror").length > 0) {
    throw new Error("Não foi possível interpretar o arquivo KML.");
  }

  const tolerance = override ?? toleranceForFile(file);
  const polygons = doc.getElementsByTagName("Polygon");
  const result = [];

  for (let i = 0; i < polygons.length; i++) {
    const polygon = polygons[i];
    // Usa apenas o anel externo (outerBoundaryIs); ignora buracos/innerBoundary.
    const outer = polygon.getElementsByTagName("outerBoundaryIs")[0];
    const coordsNode = (outer || polygon).getElementsByTagName(
      "coordinates",
    )[0];
    if (!coordsNode) continue;

    const rawPoints = dropClosingVertex(
      parseCoordinates(coordsNode.textContent),
    );
    const points = simplifyPoints(rawPoints, tolerance);
    if (points.length < 3) continue;

    // Nome vem do Placemark ancestral, se houver.
    let placemark = polygon.parentNode;
    while (placemark && placemark.nodeName !== "Placemark") {
      placemark = placemark.parentNode;
    }
    const name = placemark ? firstChildText(placemark, "name") : "";

    result.push({ name, points, rawCount: rawPoints.length });
  }

  if (result.length === 0) {
    throw new Error("Nenhum polígono encontrado no arquivo.");
  }

  return result;
}

// "[lng, lat, alt?]" -> [{ lat, long }]; descarta vértices não finitos.
function parseGeoJsonRing(ring) {
  return (ring || [])
    .map((c) => ({ lat: Number(c[1]), long: Number(c[0]) }))
    .filter((p) => Number.isFinite(p.lat) && Number.isFinite(p.long));
}

// Coleta todas as geometrias de qualquer formato de topo de GeoJSON:
// FeatureCollection, Feature, GeometryCollection ou geometria pura.
function collectGeoJsonGeometries(node, name = "") {
  const out = [];
  if (!node || typeof node !== "object") return out;

  if (node.type === "FeatureCollection") {
    (node.features || []).forEach((f) =>
      out.push(...collectGeoJsonGeometries(f)),
    );
  } else if (node.type === "Feature") {
    const props = node.properties || {};
    const fName = props.name || props.Name || props.title || "";
    out.push(...collectGeoJsonGeometries(node.geometry, fName));
  } else if (node.type === "GeometryCollection") {
    (node.geometries || []).forEach((g) =>
      out.push(...collectGeoJsonGeometries(g, name)),
    );
  } else if (node.type === "Polygon" || node.type === "MultiPolygon") {
    out.push({ geometry: node, name });
  }
  return out;
}

// Lê um arquivo GeoJSON e devolve apenas os polígonos (mesmo formato do KML).
// GeoJSON usa a ordem [lng, lat], igual ao KML — reaproveita todo o pipeline.
export async function parseGeoJsonFile(file, { tolerance: override } = {}) {
  const text = await readAsText(file);
  let data;
  try {
    data = JSON.parse(text);
  } catch (e) {
    throw new Error("Não foi possível interpretar o arquivo GeoJSON.");
  }

  const tolerance = override ?? toleranceForFile(file);
  const geometries = collectGeoJsonGeometries(data);
  const result = [];

  for (const { geometry, name } of geometries) {
    // Polygon -> anel externo é coordinates[0].
    // MultiPolygon -> cada sub-polígono vira uma área (anel externo coordinates[n][0]).
    const rings =
      geometry.type === "Polygon"
        ? [geometry.coordinates?.[0]]
        : (geometry.coordinates || []).map((poly) => poly?.[0]);

    for (const ring of rings) {
      if (!ring) continue;
      const rawPoints = dropClosingVertex(parseGeoJsonRing(ring));
      const points = simplifyPoints(rawPoints, tolerance);
      if (points.length < 3) continue;
      result.push({ name, points, rawCount: rawPoints.length });
    }
  }

  if (result.length === 0) {
    throw new Error("Nenhum polígono encontrado no arquivo.");
  }

  return result;
}

// Entrada unificada: escolhe o parser pela extensão do arquivo.
export async function parsePolygonFile(file, options) {
  const lower = (file?.name || "").toLowerCase();
  if (lower.endsWith(".geojson") || lower.endsWith(".json")) {
    return parseGeoJsonFile(file, options);
  }
  return parseKmlFile(file, options);
}

// Centroide simples (média dos vértices) — usado como coordenada do elemento.
export function polygonCentroid(points) {
  const total = points.reduce(
    (acc, p) => ({ lat: acc.lat + p.lat, long: acc.long + p.long }),
    { lat: 0, long: 0 },
  );
  return {
    lat: total.lat / points.length,
    long: total.long / points.length,
  };
}
